const hasCalculatedShadingValues = (values) =>
  Array.isArray(values) &&
  values.length >= 13 &&
  values.some((value) => {
    const number = Number(value);
    return (
      Number.isFinite(number) && number !== 1 && number >= 0 && number <= 1
    );
  });

export const detectShadingHasRun = (canvasData) => {
  if (!canvasData || typeof canvasData !== 'object') return false;

  const shadingImages = canvasData.shading_images ?? canvasData.shadingImages;
  if (typeof shadingImages === 'string' && shadingImages.trim()) {
    return true;
  }
  if (
    shadingImages &&
    typeof shadingImages === 'object' &&
    Object.keys(shadingImages).length > 0
  ) {
    return true;
  }

  if (canvasData.color_range && typeof canvasData.color_range === 'string') {
    const parts = canvasData.color_range.split(',');
    if (parts.length === 2) {
      const [minimum, maximum] = parts.map(Number);
      if (
        Number.isFinite(minimum) &&
        Number.isFinite(maximum) &&
        (minimum !== 0 || maximum !== 2450)
      ) {
        return true;
      }
    }
  }

  const segments = Array.isArray(canvasData.segments)
    ? canvasData.segments
    : [];
  return segments.some(
    (segment) =>
      segment &&
      typeof segment === 'object' &&
      (hasCalculatedShadingValues(
        segment.solar_access || segment.solarAccess
      ) ||
        hasCalculatedShadingValues(segment.tsrf) ||
        hasCalculatedShadingValues(segment.tof))
  );
};

export const resolveShadingStatusPatch = (status) => ({
  shadingStatus: status,
  ...(status === 0
    ? {
        shadingHasRun: true,
        shadingNeedsRerun: false,
        shadingRerunNoticeShown: false
      }
    : {})
});

const sameOptionalNumber = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) && !Number.isFinite(nb)) {
    return true;
  }
  return na === nb;
};

/**
 * True when roof-segment geometry / measure-optimize fields changed.
 * Edge-type (`lines`) and setback-only updates are not structural.
 */
export const isStructuralRoofSegmentChange = (prevSegments, nextSegments) => {
  if (!Array.isArray(prevSegments) || !Array.isArray(nextSegments)) {
    return true;
  }
  if (prevSegments.length !== nextSegments.length) {
    return true;
  }

  for (let i = 0; i < nextSegments.length; i++) {
    const prev = prevSegments[i] || {};
    const next = nextSegments[i] || {};
    if (String(prev.id ?? i) !== String(next.id ?? i)) {
      return true;
    }
    if (JSON.stringify(prev.geometry) !== JSON.stringify(next.geometry)) {
      return true;
    }
    if (JSON.stringify(prev.orgVertices) !== JSON.stringify(next.orgVertices)) {
      return true;
    }
    if (!sameOptionalNumber(prev.pitch, next.pitch)) {
      return true;
    }
    if (!sameOptionalNumber(prev.azimuth, next.azimuth)) {
      return true;
    }
    if (!sameOptionalNumber(prev.height, next.height)) {
      return true;
    }
    if (JSON.stringify(prev.plane) !== JSON.stringify(next.plane)) {
      return true;
    }
  }

  return false;
};

export const resolveShadingStaleTransition = (state = {}, options = {}) => {
  const hasPanels =
    Array.isArray(state.panels) &&
    state.panels.some(
      (panel) =>
        (panel?.horizontal?.points?.length || 0) > 0 ||
        (panel?.vertical?.points?.length || 0) > 0
    );
  const clearPanels = options.clearPanels !== false && hasPanels;
  const shouldNotify =
    (state.shadingHasRun || hasPanels) && !state.shadingRerunNoticeShown;
  if (!shouldNotify) {
    return {
      shouldNotify: false,
      shouldClearPanels: false,
      message: null,
      patch: {}
    };
  }

  const message = clearPanels
    ? state.shadingHasRun
      ? 'Please fill panels and re-run shading before creating a proposal'
      : 'Please fill the panels again before creating a proposal'
    : 'Please re-run shading before calculating production';

  return {
    shouldNotify: true,
    shouldClearPanels: clearPanels,
    message,
    patch: {
      ...(clearPanels ? { panels: [], productionHandled: false } : {}),
      shadingNeedsRerun: Boolean(state.shadingHasRun),
      shadingRerunNoticeShown: true
    }
  };
};
